import React from "react"
import styled, { css } from "styled-components"
import { fontSize, colors, responsive } from "buoy-theme"
import {
  Modal,
  Button,
  ControlInput,
  FormField,
  Paragraph,
  ErrorMessage,
  HeadingTwo,
} from "buoy-core"
import AccountSource from "../../account/Source"
import InterviewSource from "../../interview/Source"
import { captureException } from "../../common/error"
import { formatPhone } from "../../account/components/userDetailsUtilities"

const LegalCopy = `
  To use this service you must be age 18 or older and reside in the United States.
  By submitting your name and phone number, you agree to receive text messages from Buoy to follow up about how you're feeling.
  You will only receive messages after using Buoy's Symptom Checker.
  Message and data rates may apply. You will always have the ability to stop/opt out.
`

const ModalContent = styled.div`
  ${fontSize("bodyMain")};
  color: ${colors.neutralDarkest};
`

const CheckboxInput = styled(ControlInput)`
  cursor: pointer;
  margin-top: 2px;
`

const Legal = styled.div`
  font-size: 9px;
  color: ${colors.neutralDark};
`

const CheckboxWrap = styled.div`
  color: ${colors.neutralDarkest};
  display: flex;
  margin: 0;
`

const NameWrap = styled.div`
  margin-top: 16px;
  display: flex;
  text-overflow: ellipsis;
  justify-content: space-between;
`

const SubmitWrap = styled.div`
  margin: 15px 0;
`

const SuccessParagraph = styled(Paragraph)`
  ${responsive("margin-right", "32px", "32px", "32px", "24px")};
  margin-top: 32px;
  margin-bottom: 16px;
`

const Heading = styled(HeadingTwo)`
  text-align: unset;
  margin: 16px 0 8px;
  ${props =>
    props.thanks &&
    css`
      margin: 0;
      top: 32px;
      position: absolute;
    `};
`

const SuccessMessage = () => (
  <div>
    <Heading thanks>Thank you!</Heading>
    <SuccessParagraph>
      In a moment, you will receive a welcome text. Buoy will check in with you
      again in 3 days.
    </SuccessParagraph>
  </div>
)

const Outro = ({ success }) => (
  <div>
    <SuccessMessage />
    <Legal>This window will automatically close in a few seconds...</Legal>
  </div>
)

const Checkbox = ({ isChecked, handleChange }) => (
  <CheckboxWrap onClick={handleChange("checkbox")}>
    <CheckboxInput
      multiSelect={true}
      name="agree"
      value="agree"
      onClick={handleChange("checkbox")}
      selected={isChecked}
    />
    <Legal>
      By clicking this box, you acknowledge that you agree to Buoy's{" "}
      <a
        href="https://www.buoyhealth.com/terms/"
        target="_blank"
        rel="noopener noreferrer"
      >
        Terms of Use
      </a>{" "}
      and{" "}
      <a
        href="https://www.buoyhealth.com/privacy/"
        target="_blank"
        rel="noopener noreferrer"
      >
        Privacy Policy
      </a>
      .
    </Legal>
  </CheckboxWrap>
)

const FollowUpForm = ({
  first,
  last,
  phone,
  consent,
  handleChange,
  handleSubmit,
  disableSubmit,
  error,
}) => (
  <div>
    <Paragraph>Great! Please enter your name and telephone number.</Paragraph>
    <NameWrap>
      <FormField
        value={first.value}
        required={true}
        showError={first.edited ? !first.valid : false}
        errorMessage={"Required"}
        autoComplete="given-name"
        placeholder="First name"
        onChange={handleChange("first")}
        margin="margin-right: 10px;"
      />
      <FormField
        value={last.value}
        required={true}
        showError={last.edited ? !last.valid : false}
        errorMessage={"Required"}
        autoComplete="family-name"
        placeholder="Last name"
        onChange={handleChange("last")}
      />
    </NameWrap>
    <FormField
      value={formatPhone(phone.value)}
      required={true}
      showError={phone.edited ? !phone.valid : false}
      errorMessage={"Please enter valid phone number"}
      autoComplete="tel"
      placeholder="Phone number"
      maxLength="14"
      onChange={handleChange("phone")}
    />

    <Checkbox handleChange={handleChange} isChecked={consent} />

    <SubmitWrap>
      <Button disabled={disableSubmit()} onClick={() => handleSubmit()}>
        Submit
      </Button>
    </SubmitWrap>

    <ErrorMessage>{error}</ErrorMessage>

    <Legal>{LegalCopy}</Legal>
  </div>
)

class FollowUpModal extends React.Component {
  constructor(props) {
    super(props)
    let profile = this.props.profile[0] || {}

    this.state = {
      first: {
        value: profile.first_name || "",
        valid: undefined,
        edited: false,
      },
      last: { value: profile.last_name || "", valid: undefined, edited: false },
      phone: { value: profile.phone || "", valid: undefined, edited: false },
      consent: false,
      showIntro: true,
      showOutro: false,
      errorText: "",
    }
  }

  handleChange = field => e => {
    let isvalid
    if (field !== "checkbox") {
      isvalid = this.checkValid(field, e.target.value)
    }
    this.setState({ errorText: "" })

    // Prevent clicking link from triggering change
    if (e.target.nodeName === "A") {
      return
    }

    switch (field) {
      case "first":
        this.setState({
          first: { value: e.target.value, valid: isvalid, edited: true },
        })
        break
      case "last":
        this.setState({
          last: { value: e.target.value, valid: isvalid, edited: true },
        })
        break
      case "phone":
        this.setState({
          phone: { value: e.target.value, valid: isvalid, edited: true },
        })
        break
      case "checkbox":
        this.setState({ consent: !this.state.consent })
        break
      default:
        break
    }
  }

  handleSubmit = () => {
    this.setState({ errorText: "" })
    const profile = this.props.profile[0]

    // strip non-numerical characters from phone number
    const phone = this.state.phone.value.replace(/[^\d]/g, "")

    AccountSource.updateProfile(
      profile.id,
      this.state.first.value,
      this.state.last.value,
      profile.sex,
      profile.isself,
      profile.year,
      profile.month,
      profile.day,
      profile.age,
      Math.floor(profile.height / 12),
      profile.height % 12,
      profile.weight,
      phone
    )
      .then(() => {
        this.recordOptIn()
          .then(() => {
            this.setState({ showOutro: true })
            setTimeout(() => {
              this.props.showModal(false)
              this.props.showFollowUpCard(false)
            }, 5000)
          })
          .catch(error => {
            captureException(error)
            this.setState({
              errorText: "Error recording response, please try again.",
            })
          })
      })
      .catch(error => {
        captureException(error)
        this.setState({
          errorText: "Error recording response, please try again.",
        })
      })
  }

  handleCancel = () => {
    this.recordOptIn()
    this.props.showModal(false)
    if (this.state.showOutro) this.props.showFollowUpCard(false)
  }

  recordOptIn = () => {
    return InterviewSource.followUpOptIn(this.state.consent)
  }

  checkValid = (type, value) => {
    const phoneRegex = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/im
    switch (type) {
      case "first":
      case "last":
        if (value.length > 0) {
          return true
        }
        break
      case "phone":
        if (phoneRegex.test(value.trim())) {
          return true
        }
        break
      default:
        break
    }
    return false
  }

  disableSubmit = () => {
    const { first, last, phone, consent } = this.state
    const allValid =
      this.checkValid("first", first.value) &&
      this.checkValid("last", last.value) &&
      this.checkValid("phone", phone.value)

    if (consent && allValid) {
      return false
    }
    return true
  }

  render() {
    return (
      <Modal
        id="follow_up_modal"
        heading=""
        showSubmit={false}
        showCancel={false}
        submitEnabled={this.state.consent}
        cancelClick={() => this.handleCancel()}
      >
        <ModalContent>
          {this.state.showOutro ? (
            <Outro success={this.state.consent} />
          ) : (
            <FollowUpForm
              first={this.state.first}
              last={this.state.last}
              phone={this.state.phone}
              error={this.state.errorText}
              consent={this.state.consent}
              submitted={this.state.submitted}
              handleSubmit={this.handleSubmit}
              handleChange={this.handleChange}
              disableSubmit={this.disableSubmit}
            />
          )}
        </ModalContent>
      </Modal>
    )
  }
}

export default FollowUpModal



// WEBPACK FOOTER //
// ./assets/completion/components/FollowUpModal.js